import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Ticket, Users, Clock, TrendingUp, AlertTriangle, Activity,
  ArrowRight, Zap, Phone, Brain
} from 'lucide-react';
import { cn } from '@hts/ui';
import { useGet } from '../hooks/useApi.js';
import { PriorityBadge, SlaChip, SentimentChip, ModeBadge } from '../components/ui/StatusBadge.js';
import { KpiSkeleton, CardSkeleton } from '../components/ui/Skeleton.js';
import { RobotEmptyState } from '../components/ui/RobotEmptyState.js';
import { ErrorState } from '../components/ui/EmptyState.js';
import { relativeTime } from '../lib/format.js';
import type { Ticket as TicketType, DispatchDecision } from '../api/types.js';

interface KpiData {
  openTickets: number;
  unassigned: number;
  slaAtRisk: number;
  availableTechs: number;
  totalTechs: number;
  /** null when there is no first-response data yet. */
  avgResponseMinutes: number | null;
  /** Mean analysed-call sentiment over 7 days; null when none. */
  avgSentiment: number | null;
  shadowModeBoards: number;
}

/** Row shape of GET /api/v1/calls (real API, not the MSW mock). */
interface RecentCall {
  id: string;
  direction: string;
  callerNumber: string;
  callerName?: string | null;
  companyName?: string | null;
  sentiment?: number | null;
  startedAt: string;
}

const REFRESH_MS = 60_000;

function friendlyError(err: string | undefined, what: string): string {
  if (!err) return `Could not load ${what}.`;
  if (err.includes('403')) return `Your role does not have access to ${what}.`;
  if (err.includes('401')) return 'Your session expired. Sign in again.';
  return `Could not load ${what} (${err}).`;
}

export function DashboardPage() {
  const navigate = useNavigate();
  const kpiQ = useGet<KpiData>('/api/v1/kpi');
  // Server-side filtered: open tickets soonest-SLA first, and open unassigned.
  const slaQ = useGet<{ items: TicketType[] }>('/api/v1/tickets?closed=false&sla=risk&sort=sla&limit=5');
  const unassignedQ = useGet<{ items: TicketType[]; total: number }>('/api/v1/tickets?closed=false&unassigned=true&limit=4');
  const callsQ = useGet<{ items: RecentCall[] }>('/api/v1/calls?limit=4');
  const dispatchQ = useGet<{ items: DispatchDecision[]; pending?: number }>('/api/v1/dispatch?limit=20');
  const [lastRefresh, setLastRefresh] = React.useState(() => new Date());

  // "Live": re-poll every minute.
  const refetchAll = React.useRef<() => void>(() => undefined);
  refetchAll.current = () => {
    kpiQ.refetch(); slaQ.refetch(); unassignedQ.refetch(); callsQ.refetch(); dispatchQ.refetch();
    setLastRefresh(new Date());
  };
  React.useEffect(() => {
    const t = window.setInterval(() => refetchAll.current(), REFRESH_MS);
    return () => window.clearInterval(t);
  }, []);

  const { data: kpi, loading: kpiLoading } = kpiQ;
  const ticketsLoading = slaQ.loading;
  const callsLoading = callsQ.loading;
  const dispatchLoading = dispatchQ.loading;

  const calls = callsQ.data?.items ?? [];
  const dispatch = dispatchQ.data?.items ?? [];

  const criticalTickets = (slaQ.data?.items ?? []).filter(t => t.slaRisk !== 'ok').slice(0, 5);
  const unassigned = unassignedQ.data?.items ?? [];
  const pendingAll = dispatch.filter(d => (d.status ? d.status === 'pending' : !d.chosenTechId && !d.overriddenTo));
  const pendingCount = dispatchQ.data?.pending ?? pendingAll.length;
  const pendingDispatch = pendingAll.slice(0, 3);
  const recentCalls = calls.slice(0, 4);

  return (
    <div className="p-4 lg:p-6 space-y-6 max-w-[1600px] mx-auto">
      {/* Page title */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[hsl(var(--content))]">Dashboard</h1>
          <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="flex items-center gap-1.5 text-xs text-[hsl(var(--content-subtle))] font-medium"
            title={`Auto-refreshes every minute. Last refresh ${lastRefresh.toLocaleTimeString()}`}
          >
            <span className="h-2 w-2 rounded-full bg-brand-500" />
            Auto-refresh
          </span>
          <button
            type="button"
            onClick={() => refetchAll.current()}
            className="rounded-lg border border-[hsl(var(--border))] px-2.5 py-1 text-xs text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpiLoading ? (
          Array.from({ length: 8 }).map((_, i) => <KpiSkeleton key={i} />)
        ) : kpiQ.error ? (
          <div className="col-span-2 lg:col-span-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
            <ErrorState message={friendlyError(kpiQ.error, 'KPIs')} onRetry={kpiQ.refetch} />
          </div>
        ) : kpi ? (
          <>
            <KpiCard
              label="Open Tickets"
              value={kpi.openTickets}
              icon={<Ticket className="h-5 w-5" />}
              sub="Across all boards"
              accent="blue"
              onClick={() => navigate('/tickets')}
            />
            <KpiCard
              label="Unassigned"
              value={kpi.unassigned}
              icon={<AlertTriangle className="h-5 w-5" />}
              sub="Need assignment"
              accent={kpi.unassigned > 0 ? 'yellow' : 'green'}
              onClick={() => navigate('/tickets?unassigned=true')}
            />
            <KpiCard
              label="SLA at Risk"
              value={kpi.slaAtRisk}
              icon={<Clock className="h-5 w-5" />}
              sub="Breached or due within 4h"
              accent={kpi.slaAtRisk > 5 ? 'red' : kpi.slaAtRisk > 0 ? 'yellow' : 'green'}
              onClick={() => navigate('/tickets?sla=risk')}
            />
            <KpiCard
              label="Available Techs"
              value={`${kpi.availableTechs} / ${kpi.totalTechs}`}
              icon={<Users className="h-5 w-5" />}
              sub="Ready to assign"
              accent="blue"
              onClick={() => navigate('/people/technicians')}
            />
            <KpiCard
              label="Avg Response"
              value={kpi.avgResponseMinutes == null ? '—' : `${kpi.avgResponseMinutes}m`}
              icon={<Activity className="h-5 w-5" />}
              sub={kpi.avgResponseMinutes == null ? 'No time entries in 30 days' : 'First time entry, last 30 days'}
              accent="purple"
            />
            <KpiCard
              label="Avg Sentiment"
              value={kpi.avgSentiment == null ? '—' : `${kpi.avgSentiment.toFixed(1)} / 5`}
              icon={<TrendingUp className="h-5 w-5" />}
              sub={kpi.avgSentiment == null ? 'No analysed calls this week' : 'Call sentiment, last 7 days'}
              accent={kpi.avgSentiment == null ? 'blue' : kpi.avgSentiment >= 3.5 ? 'green' : kpi.avgSentiment >= 2.5 ? 'yellow' : 'red'}
              onClick={() => navigate('/analytics')}
            />
            <KpiCard
              label="Shadow Boards"
              value={kpi.shadowModeBoards}
              icon={<Brain className="h-5 w-5" />}
              sub="AI analyzing, no action"
              accent="blue"
              onClick={() => navigate('/admin/boards')}
            />
            <KpiCard
              label="Dispatch Queue"
              value={dispatchLoading ? '…' : pendingCount}
              icon={<Zap className="h-5 w-5" />}
              sub="Awaiting review"
              accent={pendingCount > 0 ? 'yellow' : 'green'}
              onClick={() => navigate('/dispatch')}
            />
          </>
        ) : null}
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* SLA Risk tickets */}
        <div className="lg:col-span-2 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
                <Clock className="h-4 w-4 text-red-600 dark:text-red-400" />
              </span>
              <h2 className="text-sm font-semibold text-[hsl(var(--content))]">SLA Risk</h2>
              {criticalTickets.length > 0 && (
                <span className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 px-1.5 text-[10px] font-bold text-red-700 dark:text-red-300">
                  {criticalTickets.length}
                </span>
              )}
            </div>
            <button onClick={() => navigate('/tickets?sla=risk')} className="flex items-center gap-1 text-xs text-brand-600 dark:text-brand-400 hover:underline">
              View all <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          <div className="divide-y divide-[hsl(var(--border))]">
            {ticketsLoading
              ? Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} lines={1} />)
              : slaQ.error
              ? <ErrorState message={friendlyError(slaQ.error, 'tickets')} onRetry={slaQ.refetch} />
              : criticalTickets.length === 0
              ? (
                <RobotEmptyState
                  title="No SLA risk"
                  description="No open ticket is breached or due within 24 hours."
                />
              )
              : criticalTickets.map(ticket => (
                <button
                  key={ticket.id}
                  onClick={() => navigate(`/tickets/${ticket.id}`)}
                  className="w-full text-left flex items-center gap-3 px-5 py-3 hover:bg-[hsl(var(--surface-subtle))] transition-colors"
                >
                  <PriorityBadge priority={ticket.priority} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[hsl(var(--content))] truncate">{ticket.summary}</p>
                    <p className="text-xs text-[hsl(var(--content-subtle))] truncate">{ticket.company} · {ticket.board}</p>
                  </div>
                  <SlaChip risk={ticket.slaRisk} deadline={ticket.slaDeadline} />
                </button>
              ))
            }
          </div>
        </div>

        {/* Unassigned tickets */}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </span>
              <h2 className="text-sm font-semibold text-[hsl(var(--content))]">Unassigned</h2>
              {(unassignedQ.data?.total ?? 0) > 0 && (
                <span className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30 px-1.5 text-[10px] font-bold text-amber-700 dark:text-amber-300">
                  {unassignedQ.data?.total}
                </span>
              )}
            </div>
            <button onClick={() => navigate('/tickets?unassigned=true')} className="flex items-center gap-1 text-xs text-brand-600 dark:text-brand-400 hover:underline">
              View all <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          <div className="divide-y divide-[hsl(var(--border))]">
            {unassignedQ.loading
              ? Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} lines={1} />)
              : unassignedQ.error
              ? <ErrorState message={friendlyError(unassignedQ.error, 'tickets')} onRetry={unassignedQ.refetch} />
              : unassigned.length === 0
              ? (
                <RobotEmptyState
                  title="All tickets assigned"
                  description="Every open ticket has an owner. Nice work!"
                />
              )
              : unassigned.map(ticket => (
                <button
                  key={ticket.id}
                  onClick={() => navigate(`/tickets/${ticket.id}`)}
                  className="w-full text-left flex items-center gap-3 px-5 py-3 hover:bg-[hsl(var(--surface-subtle))] transition-colors"
                >
                  <PriorityBadge priority={ticket.priority} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[hsl(var(--content))] truncate leading-tight">{ticket.summary}</p>
                    <p className="text-xs text-[hsl(var(--content-subtle))] mt-0.5">{ticket.company}</p>
                  </div>
                </button>
              ))
            }
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Dispatch queue */}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <Zap className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </span>
              <h2 className="text-sm font-semibold text-[hsl(var(--content))]">Dispatch Queue</h2>
            </div>
            <button onClick={() => navigate('/dispatch')} className="flex items-center gap-1 text-xs text-brand-600 dark:text-brand-400 hover:underline">
              Go to board <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          <div className="divide-y divide-[hsl(var(--border))]">
            {dispatchLoading
              ? Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} lines={1} />)
              : dispatchQ.error
              ? <ErrorState message={friendlyError(dispatchQ.error, 'the dispatch queue')} onRetry={dispatchQ.refetch} />
              : pendingDispatch.length === 0
              ? (
                <RobotEmptyState
                  title="No pending dispatch decisions"
                  description="The dispatch queue is clear."
                />
              )
              : pendingDispatch.map(d => (
                <button
                  key={d.id}
                  onClick={() => navigate('/dispatch')}
                  className="w-full text-left flex items-center gap-3 px-5 py-3 hover:bg-[hsl(var(--surface-subtle))] transition-colors"
                >
                  <ModeBadge mode={d.mode} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[hsl(var(--content))] truncate">{d.ticketSummary}</p>
                    <p className="text-xs text-[hsl(var(--content-subtle))]">{d.candidates[0]?.techName ? `${d.candidates[0].techName} suggested` : 'No qualified technician'} · {relativeTime(d.createdAt)}</p>
                  </div>
                  <span className="text-xs text-brand-600 dark:text-brand-400 font-medium shrink-0">Review →</span>
                </button>
              ))
            }
          </div>
        </div>

        {/* Recent calls */}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Phone className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </span>
              <h2 className="text-sm font-semibold text-[hsl(var(--content))]">Recent Calls</h2>
            </div>
            <button onClick={() => navigate('/calls')} className="flex items-center gap-1 text-xs text-brand-600 dark:text-brand-400 hover:underline">
              View all <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          <div className="divide-y divide-[hsl(var(--border))]">
            {callsLoading
              ? Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} lines={1} />)
              : callsQ.error
              ? <ErrorState message={friendlyError(callsQ.error, 'calls')} onRetry={callsQ.refetch} />
              : recentCalls.length === 0
              ? (
                <RobotEmptyState
                  title="No calls yet"
                  description="3CX calls appear here once call intake is connected."
                />
              )
              : recentCalls.map(call => (
                <button
                  key={call.id}
                  onClick={() => navigate('/calls')}
                  className="w-full text-left flex items-center gap-3 px-5 py-3 hover:bg-[hsl(var(--surface-subtle))] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[hsl(var(--content))] truncate">{call.callerName || call.callerNumber}</p>
                    <p className="text-xs text-[hsl(var(--content-subtle))] truncate">
                      {[call.companyName, call.direction, relativeTime(call.startedAt)].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  {call.sentiment != null && <SentimentChip score={call.sentiment} />}
                </button>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const ACCENT_MAP = {
  blue:   { bg: 'bg-blue-100 dark:bg-blue-900/30', icon: 'text-blue-600 dark:text-blue-400', value: 'text-blue-600 dark:text-blue-400', spark: 'text-blue-500/70 dark:text-blue-400/70' },
  green:  { bg: 'bg-green-100 dark:bg-green-900/30', icon: 'text-green-600 dark:text-green-400', value: 'text-green-600 dark:text-green-400', spark: 'text-green-500/70 dark:text-green-400/70' },
  yellow: { bg: 'bg-amber-100 dark:bg-amber-900/30', icon: 'text-amber-600 dark:text-amber-400', value: 'text-amber-600 dark:text-amber-400', spark: 'text-amber-500/70 dark:text-amber-400/70' },
  red:    { bg: 'bg-red-100 dark:bg-red-900/30', icon: 'text-red-600 dark:text-red-400', value: 'text-red-600 dark:text-red-400', spark: 'text-red-500/70 dark:text-red-400/70' },
  purple: { bg: 'bg-purple-100 dark:bg-purple-900/30', icon: 'text-purple-600 dark:text-purple-400', value: 'text-purple-600 dark:text-purple-400', spark: 'text-purple-500/70 dark:text-purple-400/70' },
};

function KpiCard({
  label, value, icon, sub, accent = 'blue', onClick,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  sub?: string;
  accent?: keyof typeof ACCENT_MAP;
  onClick?: () => void;
}) {
  const a = ACCENT_MAP[accent];
  const El = onClick ? 'button' : 'div';
  return (
    <El
      onClick={onClick}
      className={cn(
        'kpi-card rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4 shadow-sm transition-all duration-200 text-left',
        onClick && 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 active:translate-y-0'
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={cn('flex h-9 w-9 items-center justify-center rounded-xl', a.bg)}>
          <span className={a.icon}>{icon}</span>
        </div>
        {onClick ? (
          <ArrowRight className="h-3.5 w-3.5 text-[hsl(var(--content-placeholder))]" aria-hidden="true" />
        ) : null}
      </div>
      <p className={cn('text-2xl font-bold tabular-nums leading-tight', a.value)}>{value}</p>
      <p className="text-xs font-semibold text-[hsl(var(--content))] mt-1">{label}</p>
      {sub && <p className="text-xs text-[hsl(var(--content-subtle))] mt-0.5">{sub}</p>}
    </El>
  );
}
